import * as React from "react";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Table } from "components/bootstrap";
import { PaginatedList, Spinner } from "components/common";
import { FiltersRow } from "security-app/components/common";
import { useGetAssets } from "security-app/hooks/useAssetsAPI";
import { ColumnTogglerProvider, useColumnTogglerContext } from "security-app/components/common/ColumnToggler";
import { usePagination, useSetPagination } from "common/contexts";
import ListHeader from "./ListHeader";
import ListRows from "./ListRows";
import ListActionsSUI from "./ListActions.sui";
import getVisibleDetailColumnsCount, { STATIC_COLUMNS_COUNT } from "./utils/getVisibleDetailColumnsCount";
const PriorityColumnInitializer = () => {
  const { cacheId, columnsDispatch } = useColumnTogglerContext();
  useEffect(() => {
    columnsDispatch({
      type: "init",
      payload: { cacheId, initState: { priority: { visible: true, group: "Asset" } } }
    });
  }, [cacheId, columnsDispatch]);
  return null;
};
const AssetsTable = ({
  assets,
  showDetailsFor,
  loadingAssets,
  showSelection = true,
  showDeleteAction = true,
  showEditAction = true
}) => {
  var _a, _b;
  const { columns } = useColumnTogglerContext();
  const detailColumnsCount = useMemo(() => getVisibleDetailColumnsCount(columns || {}), [columns]);
  const priorityColumnVisible = (_b = (_a = columns == null ? void 0 : columns.priority) == null ? void 0 : _a.visible) != null ? _b : true;
  const staticColumnsCount = STATIC_COLUMNS_COUNT - (showSelection ? 0 : 1);
  const baseColumnsCount = staticColumnsCount + (priorityColumnVisible ? 1 : 0);
  const tableColSpan = detailColumnsCount + baseColumnsCount;
  return /* @__PURE__ */ React.createElement(Table, { condensed: true, responsive: true, "data-testid": "assets-list" }, /* @__PURE__ */ React.createElement(ListHeader, { assets, showDetailsFor, showSelection }), /* @__PURE__ */ React.createElement("tbody", null, loadingAssets ? /* @__PURE__ */ React.createElement("tr", null, /* @__PURE__ */ React.createElement("td", { colSpan: tableColSpan, "aria-label": "Spinner" }, /* @__PURE__ */ React.createElement(Spinner, { text: "Loading assets ..." }))) : /* @__PURE__ */ React.createElement(
    ListRows,
    {
      assets,
      showDetailsFor,
      showSelection,
      showDeleteAction,
      showEditAction
    }
  )));
};
const List = ({ assetType }) => {
  const localPagination = usePagination();
  const setLocalPagination = useSetPagination();
  const { id: assetId } = useParams();
  const localFilters = useMemo(() => {
    if (assetId && assetType) {
      return {
        type: [{ type: "text", label: assetType, value: assetType }],
        id: [{ type: "text", label: assetId, value: assetId }]
      };
    }
    return { type: [{ type: "text", label: assetType, value: assetType }] };
  }, [assetId, assetType]);
  const onPageChange = (newPage, newPageSize) => {
    setLocalPagination({ ...localPagination, page: newPage, perPage: newPageSize });
  };
  const { assets, pagination, loadingAssets } = useGetAssets({
    ...localPagination,
    filters: { ...localPagination.filters, ...localFilters }
  });
  const showDetailsFor = useMemo(() => ({ machine: assetType === "machine", user: assetType === "user" }), [assetType]);
  const removeFilter = (filterKey) => (e) => {
    e.preventDefault();
    const auxFilters = { ...localPagination.filters };
    delete auxFilters[filterKey];
    setLocalPagination({ ...localPagination, filters: auxFilters });
  };
  return /* @__PURE__ */ React.createElement(
    PaginatedList,
    {
      activePage: localPagination.page,
      pageSize: localPagination.perPage,
      onChange: onPageChange,
      totalItems: pagination.total,
      useQueryParameter: false,
      showPageSizeSelect: false
    },
    /* @__PURE__ */ React.createElement(ColumnTogglerProvider, { cacheId: "assetsList" }, /* @__PURE__ */ React.createElement(PriorityColumnInitializer, null), /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ListActionsSUI, { assetType }), /* @__PURE__ */ React.createElement(FiltersRow, { filters: localPagination.filters, onRemove: removeFilter })), /* @__PURE__ */ React.createElement(AssetsTable, { assets: assets || [], showDetailsFor, loadingAssets }))
  );
};
export { AssetsTable, PriorityColumnInitializer };
export default List;
